"use client";

import staticData from "@/public/data/profile/modal.json";
import Box from "@/shared/components/common/Box";
import DropdownMenu, {
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/common/drop-down";
import Flex from "@/shared/components/common/Flex";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/shared/components/common/sheet";
import Typography from "@/shared/components/common/Typography";
import { useDeviceType } from "@/shared/context/DeviceContext";
import { SignOut, User, Wallet } from "@phosphor-icons/react";
import Link from "next/link";
import { type ReactNode, useState } from "react";

interface ProfileModalProps {
  children: ReactNode;
  profileData: ProfileResult;
  logoutAction: () => void;
}

export default function ProfileModal({
  children,
  profileData,
  logoutAction,
}: ProfileModalProps) {
  const [open, setOpen] = useState(false);

  const { account, logout } = staticData.body.sections;

  return useDeviceType() === "mobile" ? (
    <Sheet open={open} onOpenChange={() => setOpen((prev) => !prev)}>
      <SheetTrigger>{children}</SheetTrigger>
      <SheetContent className="bg-background" side="top">
        <Flex className="flex-col gap-y-4 py-4 pt-5">
          <Box>
            <Flex className="justify-between items-center gap-x-2">
              <Wallet className="w-6 h-6 text-foreground" />
              <Flex className="gap-x-2">
                <Typography>{profileData.currency_name}</Typography>
                <Typography>{profileData.fund.toLocaleString()}</Typography>
              </Flex>
            </Flex>
          </Box>

          <Box>
            <Link className="w-full" href={"/profile/account"}>
              <Flex className="w-full justify-between items-center gap-x-2 h-10">
                <User className="w-5 h-5 text-foreground" />
                <Typography className="font-medium">{account.title}</Typography>
              </Flex>
            </Link>
          </Box>

          <Flex
            className="w-full justify-between items-center gap-x-2 h-10"
            onClick={logoutAction}
          >
            <SignOut className="w-5 h-5 text-red-800" />
            <Typography variant="body2" className="font-medium text-red-800">
              {logout.title}
            </Typography>
          </Flex>
        </Flex>
      </SheetContent>
    </Sheet>
  ) : (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent className="w-52 rounded-xs shadow-1">
        <DropdownMenuLabel>
          <Flex className="justify-between items-center gap-x-2">
            <Flex className="gap-x-2">
              <Typography>{profileData.currency_name}</Typography>
              <Typography>{profileData.fund.toLocaleString()}</Typography>
            </Flex>
            <Wallet className="w-6 h-6 text-foreground" />
          </Flex>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {/* Account */}
        <DropdownMenuCheckboxItem className="rounded-xs !px-2">
          <Link className="w-full" href={"/profile/account"}>
            <Flex className="w-full justify-between items-center gap-x-2">
              <Typography className="font-medium">{account.title}</Typography>
              <User className="w-5 h-5 text-foreground" />
            </Flex>
          </Link>
        </DropdownMenuCheckboxItem>
        {/* Logout */}
        <DropdownMenuCheckboxItem
          className="rounded-xs !px-2"
          onClick={logoutAction}
        >
          <Flex className="w-full justify-between items-center gap-x-2">
            <Typography className="font-medium">{logout.title}</Typography>
            <SignOut className="w-5 h-5 text-foreground" />
          </Flex>
        </DropdownMenuCheckboxItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
